import React, { useEffect, useState } from "react";
import { Table, Typography, Card, Button, Modal, Form, Input, Select, InputNumber, Space, message, Tag } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import api from "../api";
import moment from "moment";

const { Title } = Typography;
const { Option } = Select;

const PacksPage = () => {
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(false);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    const [inventario, setInventario] = useState([]);
    const [exportadores, setExportadores] = useState([]);
    const [selectedDetalles, setSelectedDetalles] = useState([]);
    const [detalleTemp, setDetalleTemp] = useState({ inventarioId: undefined, cantidadCajas: undefined });
    const totalCajas = selectedDetalles.reduce((sum, d) => sum + (d.cantidadCajas || 0), 0);
    const totalCajasPorProducto = selectedDetalles.reduce((acc, d) => {
        const prod = d.producto || "Desconocido";
        acc[prod] = (acc[prod] || 0) + (d.cantidadCajas || 0);
        return acc;
    }, {});

    // Search
    const [searchText, setSearchText] = useState("");

    // Detail Modal
    const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
    const [selectedPack, setSelectedPack] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Confirmation modal
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
    const [pendingValues, setPendingValues] = useState(null);

    const fetchPacks = async () => {
        setLoading(true);
        try {
            const res = await api.get("/packs");
            setPacks(res.data || []);
        } catch (err) {
            console.error(err);
            message.error("Error al cargar los despachos");
        } finally {
            setLoading(false);
        }
    };

    const fetchInventario = async () => {
        try {
            const res = await api.get("/packs/inventario");
            setInventario(res.data || []);
        } catch (err) {
            console.error(err);
            message.error("Error al cargar el inventario para selección");
        }
    };

    const fetchExportadores = async () => {
        try {
            const res = await api.get("/exportadores");
            setExportadores(res.data.data || res.data || []);
        } catch (err) {
            console.error(err);
            message.error("Error al cargar los exportadores");
        }
    };

    useEffect(() => {
        fetchPacks();
        fetchInventario();
        fetchExportadores();
    }, []);

    const handleOpenModal = () => {
        form.resetFields();
        setSelectedDetalles([]);
        setIsModalVisible(true);
        fetchInventario(); // Refresh inventory
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
    };

    const onFinish = (values) => {
        if (selectedDetalles.length === 0) {
            message.warning("Debe agregar al menos un detalle al pack");
            return;
        }
        setPendingValues(values);
        setIsConfirmModalVisible(true);
    };

    const handleConfirmSave = async () => {
        if (!pendingValues) return;
        setSubmitting(true);
        try {
            const payload = {
                observaciones: pendingValues.observaciones,
                idExportador: pendingValues.idExportador,
                detalles: selectedDetalles.map(d => ({
                    idProductor: d.idProductor,
                    idProceso: d.idProceso,
                    producto: d.producto,
                    calibre: d.calibre,
                    kgCaja: d.kgCaja,
                    cantidadCajas: d.cantidadCajas
                }))
            };

            await api.post("/packs", payload);
            message.success("Despacho creado exitosamente");
            setIsConfirmModalVisible(false);
            setIsModalVisible(false);
            setPendingValues(null);
            fetchPacks();
            fetchInventario();
        } catch (err) {
            console.error(err);
            message.error(err.response?.data?.message || "Error al crear el despacho");
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddDetalle = (values) => {
        const invItem = inventario.find(i => i.idinventario === values.inventarioId);
        if (!invItem) return;

        const currentAddedCount = selectedDetalles
            .filter(d => d.idinventario === invItem.idinventario)
            .reduce((sum, d) => sum + d.cantidadCajas, 0);

        if (values.cantidadCajas + currentAddedCount > invItem.cantidadCajas) {
            message.error(`Stock insuficiente. Disponible: ${invItem.cantidadCajas}. Ya agregado: ${currentAddedCount}.`);
            return;
        }

        const newDetalle = {
            idKey: Date.now(), // Para la tabla temporal
            idinventario: invItem.idinventario,
            idProductor: invItem.idProductor,
            producto: invItem.producto,
            idProceso: invItem.idProceso,
            productorNombre: invItem.productorNombre,
            calibre: invItem.calibre,
            kgCaja: invItem.kgCaja,
            cantidadCajas: values.cantidadCajas,
            fechaProceso: invItem.procesoFechaFin ? invItem.procesoFechaFin.split(' ')[0] : 'S/F'
        };

        setSelectedDetalles([...selectedDetalles, newDetalle]);

        // Disable the added item from selection or reduce its available quantity
        // For simplicity, we just keep it or user can manage in UI. 
        // Ideally we should subtract from available UI quantity.
    };

    const handleRemoveDetalle = (idKey) => {
        setSelectedDetalles(selectedDetalles.filter(d => d.idKey !== idKey));
    };

    const handleViewDetails = async (idPack) => {
        setIsDetailModalVisible(true);
        setLoadingDetails(true);
        try {
            const res = await api.get(`/packs/${idPack}`);
            setSelectedPack(res.data);
        } catch (err) {
            console.error(err);
            message.error("Error al cargar el detalle del pack");
            setIsDetailModalVisible(false);
        } finally {
            setLoadingDetails(false);
        }
    };

    const columns = [
        {
            title: "ID Despacho",
            dataIndex: "idpacks",
            key: "idpacks",
        },
        {
            title: "Fecha",
            dataIndex: "fecha",
            key: "fecha",
            render: (text) => moment(text).format("YYYY-MM-DD HH:mm"),
        },
        {
            title: "Usuario",
            dataIndex: "nombreUsuario",
            key: "nombreUsuario",
        },
        {
            title: "Exportador",
            dataIndex: "exportadorNombre",
            key: "exportadorNombre",
            render: (text) => text || "N/A",
        },
        {
            title: "Código de Envío",
            dataIndex: "observaciones",
            key: "observaciones",
        },
        {
            title: "Estado",
            dataIndex: "estado",
            key: "estado",
            render: (status) => (
                <Tag color={status === 'Completado' ? 'green' : 'blue'}>
                    {status}
                </Tag>
            ),
        },
        {
            title: "Acciones",
            key: "acciones",
            render: (_, record) => (
                <Button type="link" onClick={() => handleViewDetails(record.idpacks)}>
                    Ver Detalles
                </Button>
            )
        }
    ];

    const columnsDetallesForm = [
        { title: "Fecha", dataIndex: "fechaProceso" },
        { title: "Productor", dataIndex: "productorNombre" },
        { title: "Producto", dataIndex: "producto" },
        { title: "Calibre", dataIndex: "calibre" },
        { title: "Kg Caja", dataIndex: "kgCaja" },
        { title: "Cantidad Cajas a Agregar", dataIndex: "cantidadCajas" },
        {
            title: "",
            key: "action",
            render: (_, record) => (
                <Button danger icon={<DeleteOutlined />} onClick={() => handleRemoveDetalle(record.idKey)} size="small" />
            ),
        },
    ];

    const columnsDetallesView = [
        { title: "Productor", dataIndex: "productorNombre" },
        { title: "Producto", dataIndex: "producto" },
        { title: "Calibre", dataIndex: "calibre" },
        { title: "Kg Caja", dataIndex: "kgCaja" },
        { title: "Cantidad Cajas", dataIndex: "cantidadCajas" },
    ];

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: "16px" }}>
                <Title level={3} style={{ margin: 0 }}>Despachos</Title>
                <Space wrap>
                    <Input.Search
                        placeholder="Buscar por ID, Usuario o Código de Envío"
                        allowClear
                        onSearch={value => setSearchText(value)}
                        onChange={e => setSearchText(e.target.value)}
                        style={{ width: "100%", minWidth: "200px", maxWidth: "300px" }}
                    />
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenModal}>
                        Nuevo Despacho
                    </Button>
                </Space>
            </div>

            <Card>
                <Table
                    dataSource={packs.filter(p =>
                        p.idpacks?.toString().includes(searchText) ||
                        p.nombreUsuario?.toLowerCase().includes(searchText.toLowerCase()) ||
                        p.observaciones?.toLowerCase().includes(searchText.toLowerCase()) ||
                        p.exportadorNombre?.toLowerCase().includes(searchText.toLowerCase())
                    )}
                    columns={columns}
                    rowKey="idpacks"
                    loading={loading}
                    pagination={{ pageSize: 15 }}
                    scroll={{ x: 'max-content' }}
                />
            </Card>

            <Modal
                title="Crear Nuevo Despacho"
                open={isModalVisible}
                onCancel={handleCloseModal}
                footer={null}
                width={800}
            >
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <p><strong>Usuario:</strong> {localStorage.getItem("usuario") || "N/A"}</p>
                    <Form.Item
                        name="idExportador"
                        label="Exportador"
                        rules={[{ required: true, message: "Seleccione un exportador" }]}
                    >
                        <Select placeholder="Seleccione un exportador" showSearch optionFilterProp="children">
                            {exportadores.map(exp => (
                                <Option key={exp.idexportadores} value={exp.idexportadores}>
                                    {exp.nombre}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="observaciones"
                        label="Código de Envío"
                        rules={[{ required: true, message: "Ingrese el código de envío" }]}
                    >
                        <Input.TextArea rows={3} placeholder="Ej. Código de envío para exportación" />
                    </Form.Item>

                    <Card title="Agregar Detalle" size="small" style={{ marginBottom: 16 }}>
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: 16 }}>
                            <Select
                                placeholder="Seleccione de inventario"
                                showSearch
                                optionFilterProp="label"
                                optionLabelProp="label"
                                dropdownMatchSelectWidth={false}
                                style={{ width: '100%' }}
                                value={detalleTemp.inventarioId}
                                onChange={(val) => setDetalleTemp({ ...detalleTemp, inventarioId: val })}
                            >
                                {inventario.map(inv => (
                                    <Option
                                        key={inv.idinventario}
                                        value={inv.idinventario}
                                        label={`${inv.productorNombre} - ${inv.producto} | #Secu: ${inv.procesoSecuencial} | Cal: ${inv.calibre} | ${inv.kgCaja}kg`}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0', padding: '4px 0' }}>
                                            <span>
                                                <strong>{inv.productorNombre}</strong> - {inv.producto}
                                                <small style={{ marginLeft: 8, color: '#888' }}>
                                                    ({inv.procesoFechaFin ? inv.procesoFechaFin.split(' ')[0] : 'S/F'})
                                                </small>
                                            </span>
                                            <span>
                                                <Tag color="blue">#Secu: {inv.procesoSecuencial}</Tag>
                                                <Tag color="cyan">Cal: {inv.calibre}</Tag>
                                                <Tag color="green">{inv.kgCaja}kg</Tag>
                                                <Tag color="orange">Disp: {inv.cantidadCajas}</Tag>
                                            </span>
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                            <InputNumber
                                min={1}
                                max={(() => {
                                    const invItem = inventario.find(i => i.idinventario === detalleTemp.inventarioId);
                                    if (!invItem) return undefined;
                                    const alreadyAdded = selectedDetalles
                                        .filter(d => d.idinventario === invItem.idinventario)
                                        .reduce((sum, d) => sum + d.cantidadCajas, 0);
                                    return invItem.cantidadCajas - alreadyAdded;
                                })()}
                                placeholder="Cant."
                                style={{ width: '30%' }}
                                value={detalleTemp.cantidadCajas}
                                onChange={(val) => setDetalleTemp({ ...detalleTemp, cantidadCajas: val })}
                            />
                            <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    if (!detalleTemp.inventarioId || !detalleTemp.cantidadCajas) {
                                        message.warning("Seleccione un item y cantidad");
                                        return;
                                    }
                                    handleAddDetalle(detalleTemp);
                                    setDetalleTemp({ inventarioId: undefined, cantidadCajas: undefined });
                                }}
                            >
                                Agregar
                            </Button>
                        </div>

                        <Table
                            dataSource={selectedDetalles}
                            columns={columnsDetallesForm}
                            rowKey="idKey"
                            pagination={false}
                            size="small"
                        />

                        <div style={{ 
                            marginTop: 16, 
                            padding: '12px 16px', 
                            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', 
                            borderRadius: '8px', 
                            border: '1px solid #bae6fd',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: 600, color: '#0369a1', fontSize: '14px' }}>
                                    Total de cajas agregadas:
                                </span>
                                <span style={{ 
                                    fontSize: '18px', 
                                    color: '#0369a1',
                                    fontWeight: 'bold',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}>
                                    {totalCajas} {totalCajas === 1 ? 'caja' : 'cajas'}
                                </span>
                            </div>
                            {Object.keys(totalCajasPorProducto).length > 0 && (
                                <div style={{ 
                                    marginTop: 8, 
                                    paddingTop: 8, 
                                    borderTop: '1px dashed #bae6fd',
                                }}>
                                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#0369a1', marginBottom: 4 }}>
                                        Cajas por producto:
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {Object.entries(totalCajasPorProducto).map(([prod, cant]) => (
                                            <Tag color="blue" key={prod} style={{ margin: 0 }}>
                                                <strong>{prod}:</strong> {cant} {cant === 1 ? 'caja' : 'cajas'}
                                            </Tag>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>

                    <div style={{ textAlign: "right" }}>
                        <Space>
                            <Button onClick={handleCloseModal}>Cancelar</Button>
                            <Button type="primary" htmlType="submit" loading={submitting}>Crear Despacho</Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            {/* Detail Modal */}
            <Modal
                title={`Detalle Despacho ${selectedPack?.idpacks || ''}`}
                open={isDetailModalVisible}
                onCancel={() => setIsDetailModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsDetailModalVisible(false)}>
                        Cerrar
                    </Button>
                ]}
                width={700}
            >
                {loadingDetails ? <p>Cargando detalles...</p> : (
                    selectedPack && (
                        <div>
                            <p><strong>Fecha:</strong> {moment(selectedPack.fecha).format("YYYY-MM-DD HH:mm")}</p>
                            <p><strong>Estado:</strong> <Tag color={selectedPack.estado === 'Completado' ? 'green' : 'blue'}>{selectedPack.estado}</Tag></p>
                            <p><strong>Exportador:</strong> {selectedPack.exportadorNombre || "N/A"}</p>
                            <p><strong>Código de Envío:</strong> {selectedPack.observaciones}</p>

                            <Title level={5} style={{ marginTop: 16 }}>Detalles del Pack</Title>
                            <Table
                                dataSource={selectedPack.detalles || []}
                                columns={columnsDetallesView}
                                rowKey="idpack_detalles"
                                pagination={false}
                                size="small"
                            />

                            <div style={{ 
                                marginTop: 16, 
                                padding: '12px 16px', 
                                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', 
                                borderRadius: '8px', 
                                border: '1px solid #bae6fd',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: 600, color: '#0369a1', fontSize: '14px' }}>
                                        Total de cajas en este pack:
                                    </span>
                                    <span style={{ 
                                        fontSize: '18px', 
                                        color: '#0369a1',
                                        fontWeight: 'bold',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        {(() => {
                                            const tot = selectedPack.detalles?.reduce((sum, d) => sum + (d.cantidadCajas || 0), 0) || 0;
                                            return `${tot} ${tot === 1 ? 'caja' : 'cajas'}`;
                                        })()}
                                    </span>
                                </div>
                                {(() => {
                                    const totals = selectedPack.detalles?.reduce((acc, d) => {
                                        const prod = d.producto || "Desconocido";
                                        acc[prod] = (acc[prod] || 0) + (d.cantidadCajas || 0);
                                        return acc;
                                    }, {}) || {};
                                    return Object.keys(totals).length > 0 ? (
                                        <div style={{ 
                                            marginTop: 8, 
                                            paddingTop: 8, 
                                            borderTop: '1px dashed #bae6fd',
                                        }}>
                                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#0369a1', marginBottom: 4 }}>
                                                Cajas por producto:
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                {Object.entries(totals).map(([prod, cant]) => (
                                                    <Tag color="blue" key={prod} style={{ margin: 0 }}>
                                                        <strong>{prod}:</strong> {cant} {cant === 1 ? 'caja' : 'cajas'}
                                                    </Tag>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null;
                                })()}
                            </div>
                        </div>
                    )
                )}
            </Modal>

            {/* Confirmation Modal */}
            <Modal
                title="Confirmar Despacho"
                open={isConfirmModalVisible}
                onOk={handleConfirmSave}
                onCancel={() => {
                    setIsConfirmModalVisible(false);
                    setPendingValues(null);
                }}
                okText="Sí, guardar"
                cancelText="Cancelar"
                confirmLoading={submitting}
                zIndex={1100}
            >
                <p>¿Está seguro de que desea guardar este nuevo despacho?</p>
            </Modal>
        </div>
    );
};

export default PacksPage;
